// The "View As" launcher modal — system_admin only, see CLAUDE.md's View As
// invariant. Picking a user starts a read-only session where every request
// is served exactly as that person would see it (AuthContext.startViewAs);
// the actual enforcement lives entirely server-side (firebase_auth.py) — this
// component is just the picker.
import { useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { fetchUsers } from "../api/users";
import { roleLabel } from "../constants/roles";
import { matchesSearch } from "../utils/searchMatch";
import { inputCls, labelCls, errorBannerCls, modalPanelCls } from "../styles/ui";
import { X } from "lucide-react";
import Modal from "./Modal";

export default function ViewAsPicker({ onClose }) {
  const { me, startViewAs } = useAuth();
  const [query, setQuery] = useState("");
  const [error, setError] = useState(null);
  // A ref, not just `saving` state — a second click landing before the first
  // request resolves must not fire a second startViewAs (same double-submit
  // reasoning as every create form in this app).
  const startingRef = useRef(false);
  const [starting, setStarting] = useState(null); // the uid being started

  const { data: users, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: () => fetchUsers(),
  });

  const candidates = useMemo(() => {
    const list = (users || []).filter((u) => u.id !== me?.uid);
    if (!query.trim()) return list;
    return list.filter((u) => matchesSearch(u, query));
  }, [users, query, me]);

  async function pick(user) {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(user.id);
    setError(null);
    try {
      await startViewAs(user.id);
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Could not start viewing as that user."
      );
    } finally {
      startingRef.current = false;
      setStarting(null);
    }
  }

  return (
    <Modal bare fullBleed layer="dialog" align="center" closeOnBackdrop={false} onClose={onClose}>
      <div className={`${modalPanelCls} w-full h-full overflow-hidden flex flex-col sm:h-auto sm:max-h-[80vh] sm:max-w-md`}>
        <div className="flex items-center justify-between border-b border-line px-5 py-3 shrink-0">
          <h2 className="text-sm font-semibold text-ink">View As</h2>
          <button type="button" onClick={onClose} className="inline-flex items-center justify-center text-ink-muted hover:text-ink p-2.5 sm:p-0 leading-none" aria-label="Close"><X className="w-4 h-4" aria-hidden="true" /></button>
        </div>

        <div className="p-5 pb-3 shrink-0 space-y-3">
          <p className="text-xs text-ink-muted">
            See exactly what another user sees — their nav, their permissions, their own data.
            Read-only: nothing you click while viewing as them can change anything.
          </p>
          {error && <div className={errorBannerCls}>{error}</div>}
          <div>
            <label className={labelCls}>Search</label>
            <input
              className={inputCls}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, email, or role"
              autoFocus
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {isLoading ? (
            <p className="text-sm text-ink-muted px-3 py-4">Loading users…</p>
          ) : candidates.length === 0 ? (
            <p className="text-sm text-ink-muted px-3 py-4">No matching users.</p>
          ) : (
            candidates.map((u) => (
              <button
                key={u.id}
                type="button"
                disabled={startingRef.current}
                onClick={() => pick(u)}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-md text-left
                           hover:bg-surface-inset disabled:opacity-50 transition-colors"
              >
                <span className="min-w-0">
                  <span className="block text-sm text-ink truncate">{u.name || u.email}</span>
                  <span className="block text-xs text-ink-muted truncate">
                    {u.email}
                    {u.is_placeholder ? " · not signed in" : ""}
                  </span>
                </span>
                <span className="shrink-0 flex items-center gap-2">
                  <span className="text-xs text-ink-muted">{roleLabel(u.role)}</span>
                  {starting === u.id && <span className="text-xs text-ink-muted">Starting…</span>}
                </span>
              </button>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
}
